import React, { useState, useEffect } from "react";
import ContactUs from "../Components/ContactUs";
import {
  AppBar,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Typography,
  useTheme,
  Snackbar,
  Alert,
  CardContent,
  Avatar,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  IconButton,
  ListItemText,
  Stack,
  Chip,
  GlobalStyles,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import BarChartIcon from "@mui/icons-material/BarChart";
import MapIcon from "@mui/icons-material/Map";
import MenuIcon from "@mui/icons-material/Menu";
import TvIcon from "@mui/icons-material/Tv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ShareIcon from "@mui/icons-material/Share";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import UpdateIcon from "@mui/icons-material/Update";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InsightsIcon from "@mui/icons-material/Insights";
import AssignmentIcon from "@mui/icons-material/Assignment";
import RateReviewIcon from "@mui/icons-material/RateReview";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { Link } from "react-router-dom";
import Footer from "../Components/Footer";
import HomeSlider from "../Components/HomeSlider";
import HeroDataBackground from "../Components/HeroDataBackground";

const StyledHeroSection = styled(Box)(({ theme }) => ({
  minHeight: "calc(100vh - 80px)",
  backgroundColor: "#f5f7ff",
  display: "flex",
  alignItems: "center",
  position: "relative",
  overflow: "hidden",
  isolation: "isolate",
  padding: theme.spacing(6, 0),
  // Faint dot grid that fades out toward the edges
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    zIndex: -1,
    backgroundImage: `radial-gradient(${alpha(theme.palette.primary.dark, 0.14)} 1px, transparent 1px)`,
    backgroundSize: "28px 28px",
    maskImage: "radial-gradient(ellipse at center, #000 30%, transparent 75%)",
    WebkitMaskImage:
      "radial-gradient(ellipse at center, #000 30%, transparent 75%)",
  },
}));

const StyledSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  scrollMarginTop: "80px",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(7, 0),
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  height: "100%",
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 12px 30px rgba(0, 0, 0, 0.08)",
  },
}));

const pillButtonSx = { px: 4, py: 1.5, borderRadius: 8 };

const scrollToSection = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

function SectionHeading({ overline, title, subtitle }) {
  return (
    <Box textAlign="center" mb={6} className="animate-on-scroll">
      {overline && (
        <Typography
          variant="overline"
          sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 2 }}
        >
          {overline}
        </Typography>
      )}
      <Typography
        variant="h3"
        component="h2"
        sx={{ fontWeight: 700, fontSize: { xs: "2rem", md: "2.75rem" }, mb: 2 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="h6"
          component="p"
          sx={{
            color: "text.secondary",
            fontWeight: 400,
            maxWidth: 720,
            mx: "auto",
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// Decorative TV frame previewing the slideshow
function TvPreview() {
  return (
    <Box
      component={Link}
      to="/slide-show"
      aria-label="Launch slideshow"
      sx={{ display: "block", textDecoration: "none" }}
    >
      <Box
        sx={{
          bgcolor: "#1a1a1a",
          borderRadius: 3,
          p: 1.5,
          boxShadow: "0 25px 60px rgba(0,0,0,0.35)",
          transition: "transform 0.3s ease",
          "&:hover": { transform: "scale(1.02)" },
        }}
      >
        <Box
          sx={{
            aspectRatio: "16 / 9",
            borderRadius: 1.5,
            bgcolor: "#f8f9fe",
            display: "flex",
            flexDirection: "column",
            p: { xs: 2, sm: 3 },
            gap: 2,
          }}
        >
          <Box
            sx={{
              height: 10,
              width: "45%",
              bgcolor: "primary.main",
              borderRadius: 1,
            }}
          />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "flex-end",
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            {[45, 70, 55, 85, 60, 95, 75].map((height, i) => (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  height: `${height}%`,
                  borderRadius: "4px 4px 0 0",
                  bgcolor: i === 5 ? "#ff9800" : "primary.light",
                }}
              />
            ))}
          </Box>
          <Stack direction="row" spacing={0.75} justifyContent="center">
            {[0, 1, 2, 3].map((dot) => (
              <Box
                key={dot}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: dot === 0 ? "primary.main" : "grey.400",
                }}
              />
            ))}
          </Stack>
        </Box>
      </Box>
      <Box sx={{ width: "18%", height: 24, mx: "auto", bgcolor: "#2b2b2b" }} />
      <Box
        sx={{
          width: "36%",
          height: 8,
          mx: "auto",
          bgcolor: "#2b2b2b",
          borderRadius: 1,
        }}
      />
    </Box>
  );
}

const Home = () => {
  const theme = useTheme();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Reveal elements as they enter the viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 },
    );
    document
      .querySelectorAll(".animate-on-scroll")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <MapIcon />,
      title: "Interactive Maps",
      description:
        "Explore regional health data such as disease prevalence, immunization coverage, and facility distribution on interactive maps.",
    },
    {
      icon: <BarChartIcon />,
      title: "Key Performance Indicators",
      description:
        "See essential health statistics at a glance, including maternal health, vaccination coverage, and disease case counts.",
    },
    {
      icon: <AccountTreeIcon />,
      title: "Program Dashboards",
      description:
        "Health data organized into dedicated dashboards for each program, with related charts grouped by topic.",
    },
    {
      icon: <FileDownloadIcon />,
      title: "Download Data",
      description:
        "Export any chart as an image, or download the underlying data as CSV or Excel for your own analysis.",
    },
    {
      icon: <ShareIcon />,
      title: "Share Visualizations",
      description:
        "Share a direct link to any visualization with colleagues, partners, and the public.",
    },
    {
      icon: <PhoneAndroidIcon />,
      title: "Works on Any Device",
      description:
        "A responsive experience on desktop, tablet, and mobile, from the office to the field.",
    },
  ];

  const steps = [
    {
      icon: <DashboardIcon />,
      title: "Choose a dashboard",
      description: "Select the health program dashboard you are interested in.",
    },
    {
      icon: <InsightsIcon />,
      title: "Pick a chart group",
      description:
        "Each dashboard organizes its charts into groups of related topics. Pick one to view.",
    },
    {
      icon: <FileDownloadIcon />,
      title: "Download & share",
      description:
        "Export the data or share a link with colleagues, partners, and the public.",
    },
  ];

  const tvHighlights = [
    {
      icon: <AutorenewIcon />,
      text: "Visualizations rotate automatically every few seconds",
    },
    {
      icon: <FullscreenIcon />,
      text: "Full-screen layout designed for TVs, projectors, and large monitors",
    },
    {
      icon: <TouchAppIcon />,
      text: "Hands-free: no interaction needed once it is running",
    },
    {
      icon: <UpdateIcon />,
      text: "Always shows the latest published data",
    },
  ];

  const getInvolved = [
    {
      icon: <AssignmentIcon />,
      title: "Request Data",
      description:
        "Need data that isn't on the dashboard? Submit a data request to the Ministry.",
      to: "/request-form",
      action: "Submit a request",
    },
    {
      icon: <RateReviewIcon />,
      title: "Share Feedback",
      description:
        "Tell us how we can make the dashboard more useful for you and your community.",
      to: "/feedback",
      action: "Give feedback",
    },
  ];

  const testimonials = [
    {
      name: "Placeholder Name",
      role: "Ministry of Health",
      quote:
        "lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
      avatar: "/favicon-144x144.png",
    },
    {
      name: "Placeholder Name",
      role: "Strategic Affairs Executive Office, Ministry of Health",
      quote:
        "lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
      avatar: "/favicon-144x144.png",
    },
  ];

  React.useEffect(() => {
    // get the url and identify if an id is set and scroll to the element
    const url = window.location.href;
    if (url.split("#").length > 1) {
      document
        .getElementById(decodeURIComponent(url.split("#")[1]).toLowerCase())
        ?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const navigationItems = [
    { label: "Dashboard", to: "/dashboard" },
    { label: "Features", id: "features" },
    { label: "TV Mode", id: "tv-mode" },
    { label: "Key Messages", id: "key messages" },
    { label: "Contact", id: "contact" },
  ];

  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const renderNavItems = () => (
    <List sx={{ width: 260 }}>
      {navigationItems.map((item) =>
        item.to ? (
          <ListItemButton key={item.label} component={Link} to={item.to}>
            <ListItemText primary={item.label} />
          </ListItemButton>
        ) : (
          <ListItemButton
            key={item.label}
            onClick={() => {
              setMobileOpen(false);
              scrollToSection(item.id);
            }}
          >
            <ListItemText primary={item.label} />
          </ListItemButton>
        ),
      )}
      <ListItemButton component={Link} to="/slide-show">
        <ListItemIcon>
          <TvIcon color="primary" />
        </ListItemIcon>
        <ListItemText primary="Launch Slideshow" />
      </ListItemButton>
    </List>
  );

  //load title variable from .env file
  const title = process.env.REACT_APP_TITLE;

  return (
    <Box>
      <GlobalStyles
        styles={{
          ".animate-on-scroll": {
            opacity: 0,
            transform: "translateY(20px)",
            transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
          },
          ".animate-on-scroll.visible": {
            opacity: 1,
            transform: "translateY(0)",
          },
        }}
      />

      <AppBar position="sticky" elevation={2}>
        <Box
          sx={{
            background: "url(/ethiopian-flag.webp)",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPositionX: "right",
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Container maxWidth="xl">
            <Box
              py={1.5}
              pr={{ xs: 0, md: 12 }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box
                component={Link}
                to="/"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  color: "inherit",
                  textDecoration: "none",
                }}
              >
                <img
                  style={{
                    backgroundColor: "#fff",
                    boxShadow: "0 0 10px rgb(255 255 255 / 50%)",
                    borderRadius: "50%",
                  }}
                  src="/mini-moh.png"
                  alt="moh logo"
                />
                <Typography
                  component="span"
                  variant="h6"
                  noWrap
                  sx={{
                    fontSize: "1.2rem",
                    marginLeft: "10px",
                    fontWeight: "normal",
                  }}
                >
                  {title}
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ display: { xs: "none", md: "flex" } }}
              >
                {navigationItems.map((item) =>
                  item.to ? (
                    <Button
                      key={item.label}
                      component={Link}
                      to={item.to}
                      sx={{ color: "#fff" }}
                    >
                      {item.label}
                    </Button>
                  ) : (
                    <Button
                      key={item.label}
                      sx={{ color: "#fff" }}
                      onClick={() => scrollToSection(item.id)}
                    >
                      {item.label}
                    </Button>
                  ),
                )}
              </Stack>
              <IconButton
                aria-label="Open navigation menu"
                sx={{ display: { xs: "flex", md: "none" }, color: "#fff" }}
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Container>
        </Box>
      </AppBar>
      <Drawer anchor="right" open={mobileOpen} onClose={handleDrawerToggle}>
        {renderNavItems()}
      </Drawer>

      <StyledHeroSection>
        <HeroDataBackground />
        <Container maxWidth="xl">
          <HomeSlider></HomeSlider>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            mt={5}
          >
            <Button
              component={Link}
              to="/dashboard"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={pillButtonSx}
            >
              Open Full Dashboard
            </Button>
            <Button
              component={Link}
              to="/slide-show"
              variant="outlined"
              size="large"
              startIcon={<TvIcon />}
              sx={{ ...pillButtonSx, bgcolor: "#fff" }}
            >
              Launch TV Slideshow
            </Button>
          </Stack>
        </Container>
      </StyledHeroSection>

      <StyledSection id="features">
        <Container>
          <SectionHeading
            overline="What you can do"
            title="Features"
            subtitle="Everything you need to explore, understand, and share Ethiopia's public health data."
          />
          <Grid container spacing={3}>
            {features.map((feature) => (
              <Grid item xs={12} sm={6} md={4} key={feature.title}>
                <FeatureCard className="animate-on-scroll">
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: "primary.main",
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    fontWeight={600}
                  >
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {feature.description}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </StyledSection>

      <StyledSection sx={{ backgroundColor: "#f5f8fc" }}>
        <Container>
          <SectionHeading
            overline="Getting started"
            title="How It Works"
            subtitle="Find the information you need in three simple steps."
          />
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={4} key={step.title}>
                <Box textAlign="center" className="animate-on-scroll">
                  <Box
                    sx={{ position: "relative", display: "inline-flex", mb: 2 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "primary.main",
                        width: 72,
                        height: 72,
                        "& svg": { fontSize: 32 },
                      }}
                    >
                      {step.icon}
                    </Avatar>
                    <Avatar
                      sx={{
                        position: "absolute",
                        top: -4,
                        right: -4,
                        width: 28,
                        height: 28,
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        bgcolor: "#ff9800",
                        border: "2px solid #fff",
                      }}
                    >
                      {index + 1}
                    </Avatar>
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    fontWeight={600}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ maxWidth: 320, mx: "auto" }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </StyledSection>

      <StyledSection
        id="tv-mode"
        sx={{
          color: "#fff",
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        }}
      >
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6} className="animate-on-scroll">
              <Chip
                icon={<TvIcon sx={{ color: "#fff !important" }} />}
                label="TV & Large Screen Mode"
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  fontWeight: 600,
                  mb: 2,
                }}
              />
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.75rem" },
                  mb: 2,
                }}
              >
                Put health data on the big screen
              </Typography>
              <Typography
                variant="h6"
                component="p"
                sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 400, mb: 3 }}
              >
                Display key indicators in offices, health facilities, meeting
                rooms, and public spaces with an auto-playing slideshow built
                for TVs and large monitors.
              </Typography>
              <Stack spacing={1.5} mb={4}>
                {tvHighlights.map((highlight) => (
                  <Stack
                    key={highlight.text}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                  >
                    <Box sx={{ display: "flex", color: "#ffcc80" }}>
                      {highlight.icon}
                    </Box>
                    <Typography>{highlight.text}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  component={Link}
                  to="/slide-show"
                  variant="contained"
                  size="large"
                  startIcon={<TvIcon />}
                  sx={{
                    ...pillButtonSx,
                    backgroundColor: "#ff9800",
                    "&:hover": { backgroundColor: "#f57c00" },
                  }}
                >
                  Launch Slideshow
                </Button>
                <Button
                  href="/slide-show"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="large"
                  sx={{
                    ...pillButtonSx,
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.6)",
                    "&:hover": {
                      borderColor: "#fff",
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Open in New Window
                </Button>
              </Stack>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)", mt: 2 }}
              >
                Tip: click the slideshow header to switch to full screen.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6} className="animate-on-scroll">
              <TvPreview />
            </Grid>
          </Grid>
        </Container>
      </StyledSection>

      <StyledSection id="key messages">
        <Container>
          <SectionHeading overline="From our leadership" title="Key Messages" />
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  className="animate-on-scroll"
                  sx={{
                    height: "100%",
                    borderRadius: 4,
                    boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
                    borderTop: `4px solid ${theme.palette.primary.main}`,
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <FormatQuoteIcon
                      sx={{
                        fontSize: 48,
                        color: alpha(theme.palette.primary.main, 0.25),
                        transform: "scaleX(-1)",
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontStyle: "italic", lineHeight: 1.8, mb: 3 }}
                    >
                      {testimonial.quote}
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Avatar
                        src={testimonial.avatar}
                        sx={{ width: 56, height: 56, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </StyledSection>

      <StyledSection sx={{ backgroundColor: "#f5f8fc" }}>
        <Container>
          <SectionHeading
            overline="Get involved"
            title="Help Us Improve"
            subtitle="This dashboard is built for the public. Request the data you need or tell us what you think."
          />
          <Grid container spacing={3} justifyContent="center">
            {getInvolved.map((item) => (
              <Grid item xs={12} sm={6} md={5} key={item.title}>
                <FeatureCard className="animate-on-scroll">
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: "primary.main",
                        width: 56,
                        height: 56,
                      }}
                    >
                      {item.icon}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        component="h3"
                        gutterBottom
                        fontWeight={600}
                      >
                        {item.title}
                      </Typography>
                      <Typography color="text.secondary" mb={2}>
                        {item.description}
                      </Typography>
                      <Button
                        component={Link}
                        to={item.to}
                        endIcon={<ArrowForwardIcon />}
                        sx={{ px: 0 }}
                      >
                        {item.action}
                      </Button>
                    </Box>
                  </Stack>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </StyledSection>

      <StyledSection id="contact">
        <ContactUs setSnackbar={setSnackbar}></ContactUs>
      </StyledSection>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </Box>
  );
};

export default Home;
